<template lang="pug">
.game-card(ref="gameCard", :class="[dimension ? dimension : size, { 'has-game-name': showGameName }]")
  .game-card-wrapper.relative
    button.gtm-game.relative.w-full.h-full.cursor-pointer.text-primary-text(type="button", :disabled="status === 'disabled' || isJackpotMysteryCard", :data-location="gtmLocation", :data-provider="detail.providerId", :data-game-id="detail.gameId", :data-game-name="detail.name['en-US']", @click="click")
      .cursor-default(v-if="isJackpotMysteryCard")
        img.game-card-game.w-full.h-full.object-contain.inactive(:src="detail.image", loading="lazy")
        .w-full.h-full.absolute.inset-0.flex.items-center.justify-center.text-4xl ?
        img.game-frame.absolute.inset-0(:src="frameImage", loading="lazy")
      .pointer-events-none(v-else)
        img.game-card-game.w-full.h-full.object-contain(:src="detail.image", :class="[status]", loading="lazy", @error="handleImageError")
        .game-name.absolute.bottom-2.inset-x-0.hidden.px-2.text-center(v-if="alt") {{ detail.name[locale] || detail.name['en-US'] }}
        .game-cover.absolute.inset-1.flex.items-center.justify-center.h-fit(v-if="status === 'disabled' && showMaintenance", style="top: 50%; transform: translateY(-50%)")
          template(v-if="maintainEndTime")
            img(src="assets/images/game/game_img_maintaintime_normal.png")
            .text-maintenance.time-mode.flex.flex-col.items-center.justify-center
              span.text-maintenance-border {{ $t('common.maintenance') }}
              span.time {{ $datetime(maintainEndTime, 'YYYY/MM/DD HH:mm:ss') }}
          template(v-else)
            img(src="assets/images/game/game_img_maintain_normal.png")
            .text-maintenance.normal {{ $t('common.maintenance') }}
        .demo-game-tag.absolute.inset-x-0.bottom-0(v-if="isDemoEnable", :class="[size, { hasExtraInfo: bottomExtraInfoList.length > 0 }]") {{ $t('common.demo') }}
        img.game-frame.absolute.inset-0(:src="frameImage")
        img.game-provider.absolute.inset-x-0.bottom-0(v-if="bottomExtraInfoList.length === 0", :src="detail.providerImage")
      .absolute.inset-0.pointer-events-none(v-if="extraInfo !== null")
        .left-icon.aspect-square(v-if="leftExtraInfo && !tag")
          img(:src="leftExtraInfo")
        .bottom-icon.jackpot(v-if="tag === 'jackpot' && jackpot && isJackpotStart")
          img(:src="infoFrameImage")
          .absolute.inset-0.transition.duration-500(v-if="tag === 'jackpot' && jackpot")
            .bottom-content.absolute.inset-0.h-full.justify-center.flex
              .text-content
                jackpotCounter(v-if="jackpot", :jackpot="jackpot", :game="game")
        .bottom-icon(v-else-if="bottomExtraInfoList.length > 0")
          img(:src="infoFrameImage")
          .absolute.inset-0.transition.duration-500(v-for="(item, i) in bottomExtraInfoList", :class="[bottomIdx === i ? 'opacity-100' : 'opacity-0', 'will-change-[opacity]']")
            .absolute.inset-0.flex.justify-center(v-if="item.title")
              img(:src="item.title")
            .bottom-content.absolute.inset-0.h-full.justify-center.flex
              img(v-if="item.image", :src="item.image")
              .text-content(v-else)
                span.font-bold.text-content-size {{ item.text }}
      .tag.absolute.-start-2.-top-2.aspect-square(v-if="tagVisible && tag", :class="tag")
      .favorite.absolute.bottom-0.-end-2.cursor-pointer(v-if="hasFavorite && !isJackpotMysteryCard")
        a.block(v-if="isFavorite", @click.stop="removeFavorite")
          img.animate-heartbeat(src="assets/images/game/icontag_img_love_selected.png")
        a.block(v-else, @click.stop="addFavorite")
          img(src="assets/images/game/icontag_img_love_normal.png")
    .w-full.text-center.text-primary-text.multiline-ellipsis.text-xs.leading-3.pt-2.pb-1(v-if="showGameName && nameVisible") {{ gameName }}
</template>

<script setup>
import urlJoin from 'url-join'
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

import { useNuxtApp, useRuntimeConfig } from '#app'
import { useI18n } from '#imports'
import { useGameRestriction } from '@/composables/game/useGameRestriction'
import { useMainStore } from '@/stores/main'

const props = defineProps({
  game: {
    type: Object,
    required: true,
  },
  extraInfo: {
    type: Object,
    default: null,
  },
  isFavorite: {
    type: Boolean,
  },
  isJackpotMysteryCard: {
    type: Boolean,
    default: false,
  },
  tagVisible: {
    type: Boolean,
    default: true,
  },
  nameVisible: {
    type: Boolean,
    default: true,
  },
  alt: {
    type: Boolean,
    default: true,
  },
  hasFavorite: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    validator(value) {
      return ['sm', 'md', 'lg'].includes(value)
    },
    default: 'md',
  },
  showMaintenance: {
    type: Boolean,
    default: false,
  },
  dimension: {
    type: String,
    default: null,
  },
  clickable: {
    type: Boolean,
    default: true,
  },
  gtmLocation: String,
  gtmLocationId: [String, Number],
})

const emit = defineEmits(['play', 'add', 'remove'])

// Composables and utilities
const config = useRuntimeConfig()
const { $SoundControl, $analytics, $datetime } = useNuxtApp()
const { locale } = useI18n()
const mainStore = useMainStore()
const { setModal } = mainStore
const { playGame, enterDemoGame } = useGame()
const { createGameSuccessCallback } = useGameRouter()
const { verify } = useVerify()
const { displayGameNameUnderIcon, demoGameEnable } = useSite()
const { isGuest } = useAuthToken()
const { getBackCdnDefaultUrl, getBackCdnUrl } = useCdnAssets()
const { isGameLimited, checkGameRestriction } = useGameRestriction()

// Reactive state
const gameCard = ref(null)
const timer = ref(null)
const bottomIdx = ref(0)

// Store getters
const getTrackLocation = computed(() => mainStore.getTrackLocation)
const getJackpotList = computed(() => mainStore.getJackpotList)

// Game display properties
const gameName = computed(() => props.game?.name[locale.value] || props.game?.name?.['en-US'])

const showGameName = computed(() => Boolean(displayGameNameUnderIcon.value || ''))

// 圖片資源
const frameImage = computed(() => getBackCdnUrl(`frame/game_img_frame_normal.png`))

const detail = computed(() => {
  const lang = locale.value
  const { gameCode, providerId, gameId, mappingId, gameTypeId, status, isHot, isNew, name, portrait, maintainEndTime, enableDemo } = props.game

  return {
    gameCode,
    providerId,
    mappingId,
    gameTypeId,
    gameId,
    status,
    isHot,
    isNew,
    name,
    portrait,
    maintainEndTime,
    image: getBackCdnDefaultUrl(`games/${providerId}/${gameId}_${lang}_m@2x.png`),
    providerImage: getBackCdnUrl(`frame/game_img_${providerId.toLowerCase()}_normal.png`),
    enableDemo,
    ...props.game,
  }
})

const status = computed(() => {
  switch (detail.value?.status) {
    case 50:
      return 'disabled'
    case 99:
      return 'disabled'
    case 1:
      return 'normal'
    default:
      return 'normal'
  }
})

const maintainEndTime = computed(() => detail.value?.maintainEndTime / 1000 || null)

const clientId = computed(() => useRuntimeConfig().public.clientId)

const infoFrameImage = computed(() => getBackCdnUrl(`addition/img_framebg_normal.png`))

const jackpotId = computed(() => {
  const icon = props.extraInfo?.icon?.[0]
  return (icon || {}).name === 'jackpot' ? icon.val : null
})

const jackpot = computed(() => getJackpotList.value.find((jackpot) => jackpot.id === jackpotId.value))

const isJackpotStart = computed(() => {
  if (!jackpot.value) return false
  const now = Date.now()
  return jackpot.value.startTime <= now && jackpot.value.endTime > now
})

const leftExtraInfo = computed(() => {
  const icon = props.extraInfo?.icon?.find((icon) => icon.name !== 'jackpot')
  if (icon) {
    return getBackCdnUrl(`addition/icon_${icon.name}_${icon.val}_${locale.value}.png`)
  }
  return null
})

const bottomExtraInfoList = computed(() => {
  const list = (props.extraInfo?.navigate || []).filter((navigate) => navigate.name !== 'jackpot') || []
  const filteredData = list.filter((item) => item.name !== 'return_to_player_24H')

  return filteredData.map((item) => {
    const resource = {}

    if (item?.title) {
      resource.title = getBackCdnUrl(`addition/title_${item.name}_${locale.value}.webp`)
    }

    if (item?.mode === 'string') {
      resource.text = item.val
    } else {
      resource.image = getBackCdnUrl(`addition/navigate_${item.name}_${item.val}.webp`)
    }

    return resource
  })
})

const tag = computed(() => {
  let icon = props.extraInfo?.icon?.[0]
  if ((icon || {}).name === 'jackpot') {
    if (isJackpotStart.value) {
      return 'jackpot'
    } else {
      icon = props.extraInfo?.icon?.[1]
    }
  }

  if ((icon?.name || '').includes('dragon')) {
    return (icon?.val || '').includes('champion') ? 'dragon-champion' : 'dragon'
  }

  if ((icon?.name || '').includes('tiger')) {
    return (icon?.val || '').includes('champion') ? 'tiger-champion' : 'tiger'
  }

  if (isGameLimited(props.game)) {
    return 'limit'
  }

  if (icon) {
    switch (icon.name) {
      case 'top_game':
        return 'top'
      case 'new_game':
        return 'new'
      case 'hot_game':
        return 'hot'
    }
  }
  return null
})

const isDemoEnable = computed(() => {
  return demoGameEnable.value && props.game?.enableDemo && isGuest.value
})

const adjustMaintenance = () => {
  const parent = gameCard.value
  if (parent) {
    const parentWidth = parent.clientWidth
    parent.style.setProperty('--game-card-width', `${parentWidth}px`)
  }
}

const click = () => {
  // 如果不可點擊則不處理
  if (!props.clickable) return

  $SoundControl.sound.play('click')

  // 追蹤遊戲點擊事件
  $analytics.push({
    event: 'game_click',
    location: getTrackLocation.value,
    locationId: props.gtmLocationId,
    provider: detail.value.providerId,
    gameId: detail.value.gameId,
    gameName: detail.value.name['en-US'],
  })

  // 未登入且支援試玩，直接進入試玩
  if (isGuest.value && isDemoEnable.value) {
    const onSuccessCallback = createGameSuccessCallback()
    // enterDemoGame 失敗時內部已 handleGameError 重置 state；吞掉 rejection 避免冒泡為 unhandledrejection (Sentry CLIENT-PWA-SSR-16N)
    enterDemoGame({ game: detail.value, locale: locale.value }, onSuccessCallback).catch(() => {})
    return
  } else if (isGuest.value) {
    // 未登入但不支援試玩，顯示登入視窗
    setModal('ModalGuestLogin')
    return
  }

  const entryContainer = gameCard.value?.closest('[data-game-entry]')
  const entrySource = entryContainer ? entryContainer.getAttribute('data-game-entry') : undefined

  if (checkGameRestriction(detail.value, entrySource)) {
    return
  }

  play(entrySource)
}

const play = (entrySource) => {
  verify('ENTER_GAME').then((isVerified) => {
    if (isVerified) {
      emit('play')
      nextTick(() => {
        playGame({ game: detail.value, locale: locale.value, entry: entrySource })
      })
    }
  })
}

const addFavorite = () => {
  $SoundControl.sound.play('favoriteAdd')
  emit('add', props.game)
}

const removeFavorite = () => {
  $SoundControl.sound.play('favoriteRemove')
  emit('remove', props.game)
}

const handleImageError = (event) => {
  event.target.src = getBackCdnDefaultUrl(`games/error/404_en-US_m@2x.png`)
}

onMounted(() => {
  timer.value = setInterval(() => {
    if (bottomIdx.value + 1 < bottomExtraInfoList.value.length) {
      bottomIdx.value += 1
    } else {
      bottomIdx.value = 0
    }
  }, 3000)

  adjustMaintenance()
  window.addEventListener('resize', adjustMaintenance)
})

onBeforeUnmount(() => {
  clearInterval(timer.value)
  window.removeEventListener('resize', adjustMaintenance)
})

defineExpose({ play })
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";
.game-card.sm {
  width: 4.6875rem;
  height: 3.4375rem;
  @apply text-xs portrait:w-full portrait:h-auto;
}

.game-card.md {
  width: 8.4375rem;
  height: 6.1875rem;
  @apply text-sm portrait:w-full portrait:h-auto;
}

.game-card.md.has-game-name {
  width: 6.75rem;
  @apply portrait:w-full;
  @media (min-width: 768px) and (orientation: landscape) {
    width: 7.5rem;
  }
}

/* 桌面橫屏優化 - 合併相同斷點 */
@media (min-width: 1024px) and (orientation: landscape) {
  .game-card.sm {
    width: 100%;
  }

  .game-card.md {
    width: 100%;
    height: auto;
    aspect-ratio: 15/11;
    justify-self: center;
    font-size: 1rem;
  }

  .game-card.md.has-game-name {
    width: 100%;
    height: auto;
    aspect-ratio: 1/1;
  }
}

/* 小螢幕高度優化 */
@media screen and (max-height: 340px) {
  .game-card.md {
    width: 7.75rem;
    height: 5.75rem;
  }
}

@media screen and (max-height: 300px) {
  .game-card.md {
    width: 5rem;
    height: 5rem;
  }
}

img[lazy='error'] + .game-name {
  display: block;
}

.disabled {
  filter: saturate(0);
  cursor: default;
}

.inactive {
  filter: brightness(0);
}

/* 遊戲狀態 */
.game-provider {
  height: 18.18%;
}

/* 標籤樣式 */
.tag {
  width: 26.6667%;
  @apply bg-no-repeat bg-contain pointer-events-none;
}

.tag.top {
  background-image: url('assets/images/game/icontag_img_top_normal.png');
}

.tag.new {
  background-image: url('assets/images/game/icontag_img_new_normal.png');
}

.tag.hot {
  background-image: url('assets/images/game/icontag_img_hot_normal.png');
}

.tag.limit {
  background-image: url('assets/images/game/icontag_img_bonus_normal.png');
}

.tag.jackpot {
  background-image: url('assets/images/game/icontag_img_jackpot_normal.png');
}

.tag.dragon {
  background-image: url('assets/images/game/icontag_img_dragon_normal.png');
}

.tag.dragon-champion {
  background-image: url('assets/images/game/icontag_img_dragonrace_normal.png');
}

.tag.tiger {
  background-image: url('assets/images/game/icontag_img_tiger_normal.png');
}

.tag.tiger-champion {
  background-image: url('assets/images/game/icontag_img_tigerrace_normal.png');
}

/* 卡片容器 */
.game-card-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  @apply transition hover:scale-95;
}

.favorite {
  width: 20%;
  height: 20%;
}

/* 圖標位置 */
.left-icon {
  position: absolute;
  top: -0.5rem;
  inset-inline-start: -0.5rem;
  width: 26.67%;
}

.bottom-icon {
  position: absolute;
  bottom: -6.59%;
  inset-inline-start: 0;
  inset-inline-end: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  height: 34%;
  background-repeat: no-repeat;
  background-size: contain;
  text-align: center;
}

.text-content {
  @apply text-primary-text;
  position: absolute;
  bottom: 0;
  inset-inline-start: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 80%;
  text-align: center;
  font-size: 0.75rem;
}

.text-content-size {
  @media (max-width: 480px) {
    @apply portrait:text-[.625rem];
  }
}

/* 維護提示 */
.text-maintenance {
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  color: #000;
  font-weight: 700;
  font-size: 0.525rem;
  text-align: center;
}

.text-maintenance.time-mode {
  top: calc(var(--game-card-width) * -0.052);
  font-size: calc(var(--game-card-width) * 0.063);
  line-height: calc(var(--game-card-width) * 0.093);
}

.text-maintenance.time-mode .time {
  font-size: calc(var(--game-card-width) * 0.056);
}

.text-maintenance.normal {
  font-size: calc(var(--game-card-width) * 0.063);
  line-height: calc(var(--game-card-width) * 0.093);
}

.text-maintenance-border {
  text-shadow:
    -0.0375rem -0.0375rem 0 #fbd745,
    -0.0375rem 0 0 #fbd745,
    -0.0375rem 0.0375rem 0 #fbd745,
    0 -0.0375rem 0 #fbd745,
    0 0 0 #fbd745,
    0 0.0375rem 0 #fbd745,
    0.0375rem -0.0375rem 0 #fbd745,
    0.0375rem 0 0 #fbd745,
    0.0375rem 0.0375rem 0 #fbd745;
}

.game-card-game {
  clip-path: inset(7% 5.5% round 0.25rem);
}

/* 文字截斷 */
.multiline-ellipsis {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

@media screen and (max-height: 300px) {
  .multiline-ellipsis {
    -webkit-line-clamp: 1;
    padding-bottom: 0;
  }
}

/* Demo 遊戲標籤 */
.demo-game-tag {
  @apply text-primary-highlight mx-[0.35rem] mb-[0.35rem] rounded-bl-[0.4rem] rounded-br-[0.4rem];
  height: 30%;
  padding-top: 4px;
  font-size: calc(var(--game-card-width) * 0.08);
  background-color: rgba(0, 0, 0, 0.7);
}

.demo-game-tag.hasExtraInfo {
  height: 37%;
}

.demo-game-tag.sm {
  @apply mx-[0.25rem] mb-[0.25rem] rounded-bl-[0.25rem] rounded-br-[0.25rem];
  padding-top: 2px;
}

@media (min-width: 1024px) and (orientation: landscape) {
  .demo-game-tag {
    @apply mx-[0.3rem] mb-[0.3rem] rounded-bl-[0.3rem] rounded-br-[0.3rem];
  }
}
</style>
